import { CUSTOM_ELEMENTS_SCHEMA, Component, OnChanges, SimpleChanges, input } from '@angular/core';

import { translate } from '@jsverse/transloco';

import { AppSharedModule } from 'app/app-shared.module';
import { Constants } from 'app/constants/constants';
import { Filter } from 'app/modules/collection/services/filter.service';
import { Comparison } from 'app/providers/bridge-api/dashboard.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import SidePaneItem from '../dashboard-side-pane/SidePaneItem';
import { ComparisonHandlerService } from '../../services/comparison-handler-service';

@EnableClassTelemetry()
@Component({
  selector: 'app-comparison-side-pane-item',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './comparison-side-pane-item.component.html',
  styleUrls: ['./comparison-side-pane-item.component.scss'],
})
export class ComparisonSidePaneItemComponent extends SidePaneItem<Comparison> implements OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  comparison = input<Comparison>(undefined, { alias: 'item' });

  itemType = 'comparison';

  menuTooltip: string;
  filterDisplayValues = [];
  filterAnalyticItems = [];
  accountFieldsLoaded;

  constructor(
    private comparisonService: ComparisonHandlerService,
    private accountService: StorageAccountService
  ) {
    super();
    this.accountService.groupFieldsSub.subscribe(() => {
      this.accountFieldsLoaded = true;

      if (this.comparison()) {
        this.recalculateAnalyticItems();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.comparison?.currentValue) {
      this.filterDisplayValues = [];
      this.filterAnalyticItems = [];

      this.comparison().filters.forEach((f) => {
        this.filterDisplayValues.push(
          this.comparisonService.filterDisplayValueExtractorFn(this.comparison().type)(f)
        );

        this.filterAnalyticItems.push(this.recalculateAnalyticItem(f));
      });
    }

    if (changes.isDisabled) {
      this.menuTooltip = undefined;

      if (changes.isDisabled.currentValue) {
        this.menuTooltip = this.isDashboardLocked
          ? translate('comparison-side-panel-item-component.dashboard-locked')
          : translate('comparison-side-panel-item-component.bulk-disabled');
      }
    }
  }

  private analyticItemExtractor(analyticItem) {
    if (analyticItem === 'createdAt') {
      return translate('comparison-side-panel-item-component.creation-date');
    }

    if (analyticItem.startsWith(Constants.SECTION_PROPERTIES_PREFIX)) {
      return translate(`widgets.section-selector.${analyticItem}`);
    }

    return FieldService.getFieldLongName(analyticItem.replace('metadata.', ''));
  }

  private recalculateAnalyticItems() {
    this.filterAnalyticItems = [];

    for (const f of this.comparison().filters) {
      this.filterAnalyticItems.push(this.recalculateAnalyticItem(f));
    }
  }

  private recalculateAnalyticItem(filter) {
    if (!this.accountFieldsLoaded) {
      return;
    }

    const analyticItem = Object.values(filter).find(
      (fv: Filter[]) => Array.isArray(fv) && fv.length
    )[0].analyticItem;
    return this.analyticItemExtractor(analyticItem);
  }

  triggerItemAction(action, data?) {
    this.itemAction.emit({ action, data });
  }

  get item() {
    return this.comparison();
  }
}
